import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {Close as CloseIcon} from "@mui/icons-material";
import {Share2} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {
  getDisplayDate,
  getDisplayTime,
  convertToDate,
  isSessionDone,
} from "../../utils/dateUtils";
import SessionCard from "../SessionCard";
import {useAuth} from "../../AuthContext";
import {useTitansRealtime} from "../../hooks/useFirestore";

interface SessionDetailModalProps {
  open: boolean;
  onClose: () => void;
  session?: any; // Single session data
  sessions?: any[]; // Multiple sessions for a day
  onJoinSession?: (sessionId: string) => void;
  onLeaveSession?: (sessionId: string) => void;
}

const SessionDetailModal: React.FC<SessionDetailModalProps> = ({
  open,
  onClose,
  session,
  sessions,
  onJoinSession,
  onLeaveSession,
}) => {
  const {user, isDM, isAdmin} = useAuth();
  const {data: titans} = useTitansRealtime();
  const navigate = useNavigate();
  const [copiedOpen, setCopiedOpen] = React.useState(false);

  // Helper to resolve titan ID to titan name or get custom world name
  const getTitanName = (session: any): string => {
    // If there's a custom world name, use it
    if (session.customWorldName && session.customWorldName.trim()) {
      return session.customWorldName;
    }

    // If there's a titanId, try to resolve it
    if (session.titanId && session.titanId.trim()) {
      if (!titans || titans.length === 0) {
        return session.titanId;
      }
      const titan = titans.find((t: any) => t.id === session.titanId);
      return titan ? titan.name : session.titanId;
    }

    // Fallback
    return "Unknown World";
  };

  // If neither session nor sessions, don't render
  if (!session && (!sessions || sessions.length === 0)) return null;

  // Helper to build SessionCard props
  const buildSessionCardProps = (session: any) => {
    const isParticipating = session.players?.some(
      (p: any) => p.playerId === user?.uid
    );

    // Format date and time using the same helpers as Sessions page
    const formattedDate = getDisplayDate(session.date);
    const formattedTime = getDisplayTime(session.date);

    // Calculate duration if not provided
    let duration = session.duration;
    if (!duration && session.end) {
      // For duration calculation, we need actual Date objects
      const sessionDate = convertToDate(session.date);
      const endDate = convertToDate(session.end);

      if (!isNaN(sessionDate.getTime()) && !isNaN(endDate.getTime())) {
        const diffMs = endDate.getTime() - sessionDate.getTime();
        const diffHours = Math.round(diffMs / (1000 * 60 * 60));
        duration = `${diffHours} hour${diffHours !== 1 ? "s" : ""}`;
      }
    }

    return {
      id: session.id,
      title: session.name || "Untitled Session",
      date: formattedDate,
      time: formattedTime,
      duration: duration || "4 hours",
      location: session.location || "Online",
      titan: getTitanName(session),
      levelRange: session.levelRange || "All levels",
      maxPlayers: session.maxPlayers || 6,
      players: session.players || [],
      description: session.description || "No description available",
      isOpen: true,
      actionLabel: getActionLabel(session, isParticipating),
      onAction: getActionHandler(
        session,
        onJoinSession,
        onLeaveSession,
        isParticipating
      ),
      actionDisabled: isActionDisabled(session, isParticipating),
      secondaryActionLabel: getSecondaryActionLabel(
        session,
        isDM(),
        isAdmin(),
        user?.uid
      ),
      onSecondaryAction: getSecondaryActionHandler(session, navigate),
    };
  };

  const handleCopyLink = (targetSession?: any) => {
    try {
      const sessionToShare = targetSession || session;
      if (!sessionToShare?.id) return;
      const url = `${window.location.origin}/calendar?sessionId=${sessionToShare.id}`;
      navigator.clipboard.writeText(url).then(() => setCopiedOpen(true));
    } catch (_) {
      // ignore
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
          {sessions && sessions.length > 0
            ? "Sessions for this Day"
            : "Session Details"}
        </Box>
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
          {!sessions && session?.id && (
            <Tooltip title="Copy calendar link">
              <IconButton
                size="small"
                onClick={() => handleCopyLink(session)}
                aria-label="Copy calendar link"
              >
                <Share2 size={18} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{pt: 1}}>
        <Box sx={{"& .env-character-item": {border: "none", padding: 0}}}>
          {sessions && sessions.length > 0 ? (
            sessions.map((s, idx) => (
              <Box key={s.id || idx} className="calendar-modal-session-card">
                <SessionCard {...buildSessionCardProps(s)} />
              </Box>
            ))
          ) : (
            <Box className="calendar-modal-session-card">
              <SessionCard {...buildSessionCardProps(session)} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <Snackbar
        open={copiedOpen}
        autoHideDuration={2500}
        onClose={() => setCopiedOpen(false)}
        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
      >
        <Alert severity="success" onClose={() => setCopiedOpen(false)}>
          Link copied to clipboard
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

// Helper functions to determine action buttons based on session status and user participation
function getActionLabel(
  session: any,
  isParticipating: boolean
): string | undefined {
  if (session.status === "completed" || isSessionDone(session.date)) return undefined;
  if (isParticipating) {
    return "Withdraw";
  } else if (session.status === "available" || session.status === "upcoming") {
    return "Sign up";
  }
  return undefined;
}

function getActionHandler(
  session: any,
  onJoinSession?: (id: string) => void,
  onLeaveSession?: (id: string) => void,
  isParticipating?: boolean
) {
  return () => {
    if (isParticipating && onLeaveSession) {
      onLeaveSession(session.id);
    } else if (!isParticipating && onJoinSession) {
      onJoinSession(session.id);
    }
  };
}

function isActionDisabled(session: any, isParticipating: boolean): boolean {

  if (session.status === "completed" || isSessionDone(session.date)) return true;
  if (
    !isParticipating &&
    (session.status === "available" || session.status === "upcoming") &&
    (session.players?.length || 0) >= (session.maxPlayers || 6)
  )
    return true;
  return false;
}

function getSecondaryActionLabel(
  session: any,
  isDM: boolean,
  isAdmin: boolean,
  currentUserId?: string
): string | undefined {
  // Only show edit button if user is admin OR if user is the DM of this specific session
  if (isAdmin || (isDM && session?.dmId === currentUserId)) {
    return "Edit Session";
  }
  return undefined;
}

function getSecondaryActionHandler(session: any, navigate: any) {
  return () => {
    // Navigate to session management page with session data for editing
    navigate("/sessionmanagement", {
      state: {
        editSession: session,
        openEditForm: true,
      },
    });
  };
}

export default SessionDetailModal;
